<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceGenerationInvoices',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({});

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const panel = ref(null);
  const grid = ref(null);

  const $refs = {
    get panel() {
      return panel.value;
    },
    get grid() {
      return grid.value;
    },
  };

  const processMessage = (...args) => {
    const msg = args[0];
    if (msg.event === 'InvoiceGeneratorStatus') {
      this.$refs.panel.updateStatus(msg.data);
    }
    if (msg.event === 'InvoiceCreated') {
      this.$refs.grid.dataView.addItem(msg.data);
    }
    if (msg.event === 'InvoiceUpdated') {
      this.$refs.grid.dataView.updateItem(msg.data.id, msg.data);
    }
    if (msg.event === 'InvoiceDeleted') {
      this.$refs.grid.dataView.removeItem(msg.data.id);
    }
  };
  const onIssueInvoicesPayload = (...args) => {
    return $imodal.value
      .show({ name: 'CompleteInvoicesAlt' })
      .then((result) => {
        if (!result || result.cancel || !result.value) {
          return;
        }
        return selectedItems.value.map((p) => ({
          ...result.value,
          invoice_id: p.id,
        }));
      });
  };
  const getDownloadViewsPayload = (...args) => {
    return {
      identities: _.chain(selectedItems.value)
        .map((p) => p.id)
        .uniq()
        .value(),
    };
  };

  const gridName = ref('draftInvoices');
  const resizeTrigger = ref(0);
  const selectedItems = ref([]);
  const ts = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_3943151158818434_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
    selectable: true,
  }));
  const b_3943151158818434_columns = computed(() => [
    {
      field: 'invoice',
      name: $fn.value.pgettext('BillingList', 'Invoice'),
      params: {
        sortPath: 'invoice.invoice_number',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <a href="#" onclick="return false;">\n\
      <strong title="{{ $value.invoice_number }}">{{ $value.invoice_number }}</strong>\n\
    </a>\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'company',
      name: $fn.value.pgettext('BillingList', 'Company'),
      width: '100px',
      params: {
        sortPath: 'company.code',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'company.id',
            },
          },
        ],
      },
    },
    {
      field: 'partner',
      name: $fn.value.pgettext('BillingList', 'Partner'),
      width: '100px',
      params: {
        sortPath: 'partner.code',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'partner.id',
            },
          },
        ],
      },
    },
    {
      field: 'rentals',
      name: $fn.value.pgettext('InvoicesView', 'Rentals'),
      params: {
        sortPath: 'rentals[0].name',
      },
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'contracts',
      name: $fn.value.pgettext('InvoicesView', 'Contracts'),
      params: {
        sortPath: 'contracts[0].contract_number',
      },
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_number',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      name: $fn.value.pgettext('Invoices', 'Period start'),
      params: {
        sortPath: 'billing_period.start',
      },
      field: 'billing_period.start',
      type: 'date',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'billing_period.start',
          },
        },
      ],
    },
    {
      name: $fn.value.pgettext('Invoices', 'Period end'),
      params: {
        sortPath: 'billing_period.end',
      },
      field: 'billing_period.end',
      type: 'date',
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'billing_period.end',
          },
        },
      ],
    },
    {
      field: 'billing_category',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Billing category'),
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Net'),
      type: 'number',
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'vat_amount',
      name: $fn.value.pgettext('InvoiceBrowserView', 'VAT'),
      type: 'number',
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Amount'),
      type: 'number',
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Currency'),
    },
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Invoice date'),
      type: 'date',
    },
    {
      field: 'fulfillment_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Fulfillment date'),
      type: 'date',
    },
    {
      field: 'due_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Due date'),
      type: 'date',
    },
    {
      field: 'accounting_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Accounting date'),
      type: 'date',
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    resizeTrigger: resizeTrigger.value,
    selectedItems: selectedItems.value,
    ts: ts.value,
    ds: ds.value,
    ux: ux.value,
    b_3943151158818434_columnDefaults: b_3943151158818434_columnDefaults.value,
    b_3943151158818434_columns: b_3943151158818434_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_draft_invoices');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'resizeTrigger' },
      { name: 'selectedItems' },
      { name: 'ts' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_3943151158818434_columnDefaults', nodeId: '3943151158818434' },
      { name: 'b_3943151158818434_columns', nodeId: '3943151158818434' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      resizeTrigger,
      selectedItems,
      ts,
      ds,
      ux,
      b_3943151158818434_columnDefaults,
      b_3943151158818434_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const act_3943151158818434_setData_9tptyxx9l_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3943151158818434_showModal_pprw8f6rh_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.id,
        modal: true,
      },
    };
  };
  const inst_evh_3943151158818434_command_xs5cm29kc_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'invoice';
  };
  const act_566991778050529_reloadSlickgrid_cei54vbdx_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_566991778050529_close_onhfezm7l_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_566991778050529_showModal_mqylvfd16_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BatchInvoiceEditor',
      props: {
        invoices: selectedItems.value,
      },
    };
  };
  const act_3896679208055147_reloadSlickgrid_lnbjgsrl0_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_3896679208055147_request_wggl893vo_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'merge_invoices',
      data: {
        items: _.chain(selectedItems.value)
          .filter((p) => _.get(p, 'id') != null)
          .map((p) => p.id)
          .uniq()
          .value(),
      },
    };
  };
  const act_1267750354288436_reloadSlickgrid_6asy5dw2l_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_1267750354288436_request_9jrrg7ix0_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_draft_invoices',
      data: {
        invoices: selectedItems.value.map((p) => p.id),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_7la9uokza =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_w0gq1aus9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7la9uokza,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_w0gq1aus9],
    event: 'reload',
  });
  const act_3943151158818434_setData_9tptyxx9l = new actionTypes.SetDataAction({
    actionArgs: act_3943151158818434_setData_9tptyxx9l_getActionArgs,
  });
  const inst_evh_3943151158818434_selectedRowsChanged_4ai1hbi3p =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_setData_9tptyxx9l,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_3943151158818434_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3943151158818434_selectedRowsChanged_4ai1hbi3p],
      event: 'selectedRowsChanged',
    });
  const act_3943151158818434_showModal_pprw8f6rh =
    new actionTypes.ShowModalAction({
      actionArgs: act_3943151158818434_showModal_pprw8f6rh_getActionArgs,
    });
  const inst_evh_3943151158818434_command_xs5cm29kc =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_showModal_pprw8f6rh,
      event: 'command',
      when: inst_evh_3943151158818434_command_xs5cm29kc_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_3943151158818434_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3943151158818434_command_xs5cm29kc],
    event: 'command',
  });
  const act_566991778050529_reloadSlickgrid_cei54vbdx =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_566991778050529_reloadSlickgrid_cei54vbdx_getActionArgs,
    });
  const inst_evh_566991778050529_close_onhfezm7l =
    new actionTypes.EventHandlerImpl({
      action: act_566991778050529_reloadSlickgrid_cei54vbdx,
      event: 'close',
      when: inst_evh_566991778050529_close_onhfezm7l_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_566991778050529_showModal_mqylvfd16 =
    new actionTypes.ShowModalAction({
      actionArgs: act_566991778050529_showModal_mqylvfd16_getActionArgs,
      events: [inst_evh_566991778050529_close_onhfezm7l],
    });
  const inst_evh_566991778050529_click_sdbc73exo =
    new actionTypes.EventHandlerImpl({
      action: act_566991778050529_showModal_mqylvfd16,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_566991778050529_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_566991778050529_click_sdbc73exo],
    event: 'click',
  });
  const act_3896679208055147_reloadSlickgrid_lnbjgsrl0 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_3896679208055147_reloadSlickgrid_lnbjgsrl0_getActionArgs,
    });
  const inst_evh_3896679208055147_success_y0hbmuqe9 =
    new actionTypes.EventHandlerImpl({
      action: act_3896679208055147_reloadSlickgrid_lnbjgsrl0,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_3896679208055147_request_wggl893vo = new actionTypes.RequestAction({
    actionArgs: act_3896679208055147_request_wggl893vo_getActionArgs,
    displayName: 'mergeInvoices',
    events: [inst_evh_3896679208055147_success_y0hbmuqe9],
  });
  const inst_evh_3896679208055147_click_yme6hniuc =
    new actionTypes.EventHandlerImpl({
      action: act_3896679208055147_request_wggl893vo,
      event: 'click',
      displayName: 'mergeInvoices',
    });
  const inst_evh_3896679208055147_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3896679208055147_click_yme6hniuc],
    event: 'click',
  });
  const act_1267750354288436_reloadSlickgrid_6asy5dw2l =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1267750354288436_reloadSlickgrid_6asy5dw2l_getActionArgs,
    });
  const inst_evh_1267750354288436_success_fw6s0kcyr =
    new actionTypes.EventHandlerImpl({
      action: act_1267750354288436_reloadSlickgrid_6asy5dw2l,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1267750354288436_request_9jrrg7ix0 = new actionTypes.RequestAction({
    actionArgs: act_1267750354288436_request_9jrrg7ix0_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_1267750354288436_success_fw6s0kcyr],
  });
  const inst_evh_1267750354288436_click_3odb6eal1 =
    new actionTypes.EventHandlerImpl({
      action: act_1267750354288436_request_9jrrg7ix0,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_1267750354288436_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1267750354288436_click_3odb6eal1],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_w0gq1aus9,
    inst_evh_7315092382398562_reload,
    inst_evh_3943151158818434_selectedRowsChanged_4ai1hbi3p,
    inst_evh_3943151158818434_selectedRowsChanged,
    inst_evh_3943151158818434_command_xs5cm29kc,
    inst_evh_3943151158818434_command,
    inst_evh_566991778050529_close_onhfezm7l,
    inst_evh_566991778050529_click_sdbc73exo,
    inst_evh_566991778050529_click,
    inst_evh_3896679208055147_success_y0hbmuqe9,
    inst_evh_3896679208055147_click_yme6hniuc,
    mergeInvoices: inst_evh_3896679208055147_click_yme6hniuc,
    inst_evh_3896679208055147_click,
    inst_evh_1267750354288436_success_fw6s0kcyr,
    inst_evh_1267750354288436_click_3odb6eal1,
    deleteItems: inst_evh_1267750354288436_click_3odb6eal1,
    inst_evh_1267750354288436_click,
  };

  const evh_7315092382398562_reload_w0gq1aus9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_w0gq1aus9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_selectedRowsChanged_4ai1hbi3p = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3943151158818434_selectedRowsChanged_4ai1hbi3p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_command_xs5cm29kc = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_command_xs5cm29kc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_command = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_566991778050529_close_onhfezm7l = async (event, scope) => {
    return await actions.inst_evh_566991778050529_close_onhfezm7l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_566991778050529_click_sdbc73exo = async (event, scope) => {
    return await actions.inst_evh_566991778050529_click_sdbc73exo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_566991778050529_click = async (event, scope) => {
    return await actions.inst_evh_566991778050529_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3896679208055147_success_y0hbmuqe9 = async (event, scope) => {
    return await actions.inst_evh_3896679208055147_success_y0hbmuqe9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3896679208055147_click_yme6hniuc = async (event, scope) => {
    return await actions.inst_evh_3896679208055147_click_yme6hniuc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3896679208055147_click = async (event, scope) => {
    return await actions.inst_evh_3896679208055147_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1267750354288436_success_fw6s0kcyr = async (event, scope) => {
    return await actions.inst_evh_1267750354288436_success_fw6s0kcyr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1267750354288436_click_3odb6eal1 = async (event, scope) => {
    return await actions.inst_evh_1267750354288436_click_3odb6eal1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1267750354288436_click = async (event, scope) => {
    return await actions.inst_evh_1267750354288436_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox class="h-100">
      <ItpSidebar :default-width="400" scroll-y class="me-2">
        <UcInvoiceGeneratorControls ref="panel"> </UcInvoiceGeneratorControls>
      </ItpSidebar>
      <div class="flex-fill overflow-hidden position-relative">
        <VueSlickgrid
          :name="gridName"
          :resizeTrigger="resizeTrigger"
          :headerTitle="gettext('Draft Invoices')"
          :items="ds"
          checkbox-row-selection
          footer-totals
          export-to-csv
          :exportToCsvFilename="pgettext('ExportFilename', 'draft-invoices')"
          :columnDefaults="b_3943151158818434_columnDefaults"
          :columns="b_3943151158818434_columns"
          class="page--grid"
          ref="grid"
          @selectedRowsChanged="
            evh_3943151158818434_selectedRowsChanged($event, {})
          "
          @command="evh_3943151158818434_command($event, {})"
        >
          <template #toolbar>
            <div class="flex-grow-1 d-flex gap-2">
              <ItpButton
                :icon="$config.billingList.createItemIcon"
                :text="pgettext('Invoices', 'Bulk edit...')"
                size="sm"
                variant="light"
                :disabled="!selectedItems || !selectedItems.length"
                @click="evh_566991778050529_click($event, {})"
              >
              </ItpButton>
              <ItpButton
                :icon="$config.mergeIcon"
                :text="gettext('Merge invoices')"
                size="sm"
                variant="light"
                :disabled="!selectedItems || !selectedItems.length"
                :spinning="actions.mergeInvoices.isRunning"
                @click="evh_3896679208055147_click($event, {})"
              >
              </ItpButton>
              <ItpButton
                icon="fa-trash"
                :text="gettext('Delete')"
                size="sm"
                variant="light"
                :disabled="!selectedItems || !selectedItems.length"
                :spinning="actions.deleteItems.isRunning"
                @click="evh_1267750354288436_click($event, {})"
              >
              </ItpButton>
              <JobHandlerControl
                jobType="invoice_sync"
                load-current
                :payload="() => onIssueInvoicesPayload()"
                icon="material-symbols:cloud-sync-outline-rounded"
                :text="pgettext('DraftInvoices|Submit', 'Issue Invoices')"
                size="sm"
                :disabled="!selectedItems || !selectedItems.length"
              >
              </JobHandlerControl>
              <ItpLink
                :text="pgettext('InvoiceGeneration', 'Download invoice lines')"
                :href="$fn.urlFor('draft_invoice_lines_report')"
                button
                variant="light"
                use-auth
                :timeout="300000"
                class="d-inline-block"
              >
              </ItpLink>
              <ArtifactJob
                icon="ic:baseline-download"
                :text="gettext('Download views')"
                size="sm"
                :disabled="!selectedItems || !selectedItems.length"
                operation="download_invoice_views"
                :payload="getDownloadViewsPayload"
              >
              </ArtifactJob>
            </div>
          </template>
        </VueSlickgrid>
      </div>
    </ItpBox>
  </ItpUsercontrol>
</template>
